import { forwardRef } from 'react';
import { useTheme } from '@mui/material/styles';
import { Badge, Avatar } from '@mui/material';
//
import { PartyAvatarProps } from './types';
import { formatImageUrl } from 'src/utils/formatImageUrl';
import { getPartyColor } from 'src/utils/getPartyColor';

// ----------------------------------------------------------------------

const PartyAvatar = forwardRef<HTMLDivElement, PartyAvatarProps>(
  (
    { party = '', name = '', src = '', useGradient = true, BadgeProps, children, sx, ...other },
    ref
  ) => {
    const theme = useTheme();
    const color = getPartyColor(party);

    let renderContent = null;

    const nameSplit = name ? name.split(' ') : [];
    const firstNameChar = nameSplit[0] ? nameSplit[0].charAt(0).toUpperCase() : '';
    const lastNameChar = nameSplit[1] ? nameSplit[1].charAt(0).toUpperCase() : '';
    const charAtName = `${firstNameChar}${lastNameChar}`;

    if (src) {
      renderContent = (
        <Avatar
          ref={ref}
          sx={{
            background: useGradient
              ? `linear-gradient(135deg, ${theme.palette[color]?.light} 0%, ${theme.palette[color]?.dark} 100%)`
              : theme.palette[color]?.main,
            ...sx,
          }}
          src={formatImageUrl(src)}
          {...other}
        >
          {children}
        </Avatar>
      );
    } else {
      renderContent = (
        <Avatar
          ref={ref}
          sx={{
            color: useGradient
              ? theme.palette.background.paper
              : theme.palette[color]?.contrastText,
            background: useGradient
              ? `linear-gradient(to bottom right, ${theme.palette[color]?.light}, ${theme.palette[color]?.dark})`
              : theme.palette[color]?.main,
            fontWeight: theme.typography.fontWeightMedium,
            fontSize: 14,
            lineHeight: 22,
            ...sx,
          }}
          {...other}
        >
          {name && charAtName}
          {children}
        </Avatar>
      );
    }

    return BadgeProps ? (
      <Badge
        overlap="circular"
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        {...BadgeProps}
      >
        {renderContent}
      </Badge>
    ) : (
      renderContent
    );
  }
);

export default PartyAvatar;
